// app/url-state.ts — состояние таблицы в строке запроса (страница, размер, фильтры, сортировка, пресет).

import {
  filterClauseSchema,
  sortClauseSchema,
  type FilterClause,
  type SortClause,
} from "../domain";

export interface TableUrlState {
  page: number;
  pageSize: number;
  filters: FilterClause[];
  sort: SortClause[];
  preset: string;
  stickyColumns?: string[];
  hiddenColumns?: string[];
}

export const DEFAULT_PAGE_SIZE = 50;
export const DEFAULT_PRESET = "default";

export function parseUrlState(searchParams: URLSearchParams): TableUrlState {
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const pageSize = Math.max(
    1,
    Number(searchParams.get("size") ?? String(DEFAULT_PAGE_SIZE)) ||
      DEFAULT_PAGE_SIZE,
  );
  const preset = searchParams.get("preset") ?? DEFAULT_PRESET;

  const sortRaw = searchParams.get("sort");
  const sort: SortClause[] = sortRaw
    ? sortRaw
        .split(",")
        .map((pair) => {
          const [column, direction] = pair.split(":");
          if (!column) return null;
          const parsed = sortClauseSchema.safeParse({
            column: column.trim(),
            direction: (direction ?? "asc").trim(),
          });
          return parsed.success ? parsed.data : null;
        })
        .filter((s): s is SortClause => s !== null)
    : [];

  const filtersRaw = searchParams.get("filters");
  let filters: FilterClause[] = [];
  if (filtersRaw) {
    try {
      const parsed = filterClauseSchema.array().safeParse(JSON.parse(filtersRaw));
      if (parsed.success) filters = parsed.data;
    } catch {
      // invalid JSON — return empty filters
    }
  }

  const stickyRaw = searchParams.get("sticky");
  const stickyColumns = stickyRaw ? stickyRaw.split(",").filter(Boolean) : [];

  const hiddenRaw = searchParams.get("hidden");
  const hiddenColumns = hiddenRaw ? hiddenRaw.split(",").filter(Boolean) : [];

  return {
    page,
    pageSize,
    filters,
    sort,
    preset,
    stickyColumns,
    hiddenColumns,
  };
}

export function serializeUrlState(state: TableUrlState): string {
  const params = new URLSearchParams();
  if (state.page > 1) params.set("page", String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE)
    params.set("size", String(state.pageSize));
  if (state.preset && state.preset !== DEFAULT_PRESET)
    params.set("preset", state.preset);
  if (state.sort.length > 0) {
    params.set(
      "sort",
      state.sort.map((s) => `${s.column}:${s.direction}`).join(","),
    );
  }
  if (state.filters.length > 0) {
    params.set("filters", JSON.stringify(state.filters));
  }
  if (state.stickyColumns && state.stickyColumns.length > 0) {
    params.set("sticky", state.stickyColumns.join(","));
  }
  if (state.hiddenColumns && state.hiddenColumns.length > 0) {
    params.set("hidden", state.hiddenColumns.join(","));
  }
  const str = params.toString();
  return str ? `?${str}` : "";
}
